import FsnNetworkIcon from '@/assets/FsnNetworkIcon'
import {
  IndexDataTable,
  getIndexPaginationMeta,
  IndexTablePagination,
} from '@/components/tables/IndexTable'
import UserAvatar from '@/components/patterns/user-avatar'
import { Badge, type BadgeVariant } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { getFulfillmentTypeByValue } from '@/lib/fulfillment-type'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import { SalesOrderRefundStatus } from '@/lib/enums/SalesOrderRefundStatus'
import { cn } from '@/lib/utils'
import {
  Calendar,
  CalendarClock,
  Camera,
  CheckCircle2,
  CircleAlert,
  CircleX,
  CreditCard,
  DollarSign,
  Hash,
  ShoppingBag,
  Store,
  Truck,
  UserRound,
} from 'lucide-react'
import { memo, useMemo, type ReactElement, type ReactNode } from 'react'
import type { DisplayColumnDef } from '@tanstack/react-table'
import type { DirectionalSortConfig } from '@/components/ui/sortable-column-header'
import type { SalesOrderListItem } from '@/lib/services/sales-orders/sales-orders.types'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import type {
  IndexDataTableProps,
  IndexPaginationResponse,
  IndexTablePaginationProps,
} from '@/components/tables/IndexTable'
import { DynamicIcon } from '../ui/DynamicIcon'
import { DeliveryAddressVerificationBadge } from './DeliveryAddressVerificationBadge'
import { getSalesOrderSourceMeta } from './OrderSourceBadge'
import { SalesOrderAmountCell } from './SalesOrderAmountCell'
import {
  SalesOrderActionsMenu,
  shouldShowMarkFulfilledAction,
  shouldShowSalesOrderActions,
  useSalesOrderActionMenu,
} from './SalesOrderActionMenuItems'
import {
  getFsnNetworkFulfillmentPresentation,
  getSalesOrderFulfillmentDuePresentation,
  isCancelledSalesOrder,
  needsFsnNetworkFulfillment,
  usesCancelledPaymentPresentation,
} from './sales-order-presentation'
import { getSalesOrderRowPresentation, isSalesOrderUnread } from './sales-order-row-state'

export { getSalesOrderRowPresentation } from './sales-order-row-state'

export type SalesOrderTableOrder = SalesOrderListItem

export type SalesOrderColumn = Omit<DisplayColumnDef<SalesOrderTableOrder>, 'id' | 'cell'> & {
  id: string
  cell: (context: { row: { original: SalesOrderTableOrder } }) => ReactNode
}

type TableTone = 'destructive' | 'info' | 'muted' | 'refund' | 'success' | 'warning'
type TableTreatment = 'action' | 'quiet'
type TableIcon = NonNullable<ReturnType<typeof getSalesOrderSourceMeta>['icon']>
interface TableStatusProps {
  children?: ReactNode
  tone?: TableTone
  icon?: TableIcon
  iconName?: string
  className?: string
}

const salesOrderSortColumns = {
  id: {
    asc: 'id_asc',
    desc: 'id_desc',
    defaultSort: 'id_desc',
  },
  recipient: {
    asc: 'recipient_asc',
    desc: 'recipient_desc',
    defaultSort: 'recipient_asc',
  },
  organization: {
    asc: 'organization_asc',
    desc: 'organization_desc',
    defaultSort: 'organization_asc',
  },
  source: {
    asc: 'source_asc',
    desc: 'source_desc',
    defaultSort: 'source_asc',
  },
  fulfillmentStatus: {
    asc: 'fulfillment_status_asc',
    desc: 'fulfillment_status_desc',
    defaultSort: 'fulfillment_status_asc',
  },
  fulfillmentDueDate: {
    asc: 'fulfillment_due_date_asc',
    desc: 'fulfillment_due_date_desc',
    defaultSort: 'fulfillment_due_date_asc',
  },
  arrangementPhotoCount: {
    asc: 'arrangement_photo_count_asc',
    desc: 'arrangement_photo_count_desc',
    defaultSort: 'arrangement_photo_count_desc',
  },
  fulfillmentType: {
    asc: 'fulfillment_type_asc',
    desc: 'fulfillment_type_desc',
    defaultSort: 'fulfillment_type_asc',
  },
  paymentStatus: {
    asc: 'payment_status_asc',
    desc: 'payment_status_desc',
    defaultSort: 'payment_status_asc',
  },
  created: {
    asc: 'created_asc',
    desc: 'created_desc',
    defaultSort: 'created_desc',
  },
  total: {
    asc: 'total_asc',
    desc: 'total_desc',
    defaultSort: 'total_desc',
  },
} satisfies Record<string, DirectionalSortConfig>

const salesOrderTableBadgeVariant = {
  destructive: { action: 'destructiveSoft', quiet: 'destructiveGhost' },
  info: { action: 'infoSoft', quiet: 'infoGhost' },
  muted: { action: 'mutedSoft', quiet: 'mutedGhost' },
  refund: { action: 'refundSoft', quiet: 'refundGhost' },
  success: { action: 'successSoft', quiet: 'successGhost' },
  warning: { action: 'warningSoft', quiet: 'warningGhost' },
} satisfies Record<TableTone, Record<TableTreatment, BadgeVariant>>

function getSalesOrderTableBadgeVariant(tone: TableTone, treatment: TableTreatment): BadgeVariant {
  return (
    salesOrderTableBadgeVariant[tone]?.[treatment] ?? salesOrderTableBadgeVariant.muted[treatment]
  )
}

function SalesOrderTableIcon({
  icon: Icon,
  iconName,
  className = 'size-3.5',
}: Pick<TableStatusProps, 'icon' | 'iconName' | 'className'>): ReactElement | null {
  if (Icon) {
    return <Icon className={className} aria-hidden="true" />
  }

  if (iconName) {
    return <DynamicIcon iconName={iconName} className={className} aria-hidden="true" />
  }

  return null
}

function SalesOrderTableActionPill({
  children,
  tone = 'warning',
  icon,
  iconName,
  className = '',
}: TableStatusProps): ReactElement {
  return (
    <Badge
      data-sales-order-table-status="action"
      data-tone={tone}
      variant={getSalesOrderTableBadgeVariant(tone, 'action')}
      size="status"
      className={className}
    >
      <SalesOrderTableIcon icon={icon} iconName={iconName} className="size-3.5" />
      {children}
    </Badge>
  )
}

function SalesOrderTableQuietStatus({
  children,
  tone = 'success',
  icon = CheckCircle2,
  iconName,
  className = '',
  emphasized = false,
}: TableStatusProps & { emphasized?: boolean }): ReactElement {
  return (
    <Badge
      data-sales-order-table-status="quiet"
      data-tone={tone}
      variant={getSalesOrderTableBadgeVariant(tone, 'quiet')}
      size={emphasized ? 'status' : 'statusQuiet'}
      className={className}
    >
      <SalesOrderTableIcon icon={icon} iconName={iconName} className="size-3.5" />
      {children}
    </Badge>
  )
}

function SalesOrderTableInlineValue({
  children,
  icon,
  iconName,
  className = '',
  iconClassName = 'size-3.5',
}: Omit<TableStatusProps, 'tone'> & { iconClassName?: string }): ReactElement {
  return (
    <span
      className={cn(
        'inline-flex min-h-7 items-center gap-1.5 whitespace-nowrap text-sm font-normal text-foreground',
        className
      )}
    >
      <SalesOrderTableIcon icon={icon} iconName={iconName} className={iconClassName} />
      {children}
    </span>
  )
}

function getActionToneFromBadgeVariant(variant: unknown): TableTone {
  if (variant === 'destructive') {
    return 'destructive'
  }

  if (variant === 'success') {
    return 'success'
  }

  if (variant === 'info' || variant === 'brand') {
    return 'info'
  }

  if (variant === 'muted' || variant === 'outline' || variant === 'secondary') {
    return 'muted'
  }

  return 'warning'
}

function SalesOrderFulfillmentMethodContent({
  order,
}: {
  order: SalesOrderTableOrder
}): ReactElement | null {
  const fulfillmentType = getFulfillmentTypeByValue(order.fulfillment_type)

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5 @[40rem]/index-table:flex-nowrap">
      {order.fulfillment_type === FulfillmentType.Delivery.Value ? (
        <span
          data-sales-order-address-verification-slot="address"
          className="flex h-6 w-auto shrink-0 items-center justify-center @[40rem]/index-table:w-6"
        >
          <DeliveryAddressVerificationBadge
            deliveryAddress={order.delivery_address}
            showMobileLabel
            quietVerified
            className="shrink-0 @[40rem]/index-table:w-6 @[40rem]/index-table:gap-0 @[40rem]/index-table:px-0"
            mobileLabelClassName="@[40rem]/index-table:hidden"
          />
        </span>
      ) : (
        <span
          aria-hidden="true"
          data-sales-order-address-verification-slot="reserved"
          className="hidden h-6 w-6 shrink-0 @[40rem]/index-table:flex"
        />
      )}
      {fulfillmentType ? (
        <SalesOrderTableInlineValue
          iconName={fulfillmentType.Icon}
          className="shrink-0 font-normal"
        >
          {fulfillmentType.Name}
        </SalesOrderTableInlineValue>
      ) : null}
    </div>
  )
}

function SalesOrderFulfillmentStatusContent({
  order,
}: {
  order: SalesOrderTableOrder
}): ReactElement | null {
  const networkFulfillment = getFsnNetworkFulfillmentPresentation(order)

  if (networkFulfillment) {
    const tone = getActionToneFromBadgeVariant(networkFulfillment.variant)

    if (networkFulfillment.variant === 'success') {
      return (
        <SalesOrderTableQuietStatus icon={FsnNetworkIcon} tone="success">
          {networkFulfillment.label}
        </SalesOrderTableQuietStatus>
      )
    }

    if (tone === 'muted') {
      return (
        <SalesOrderTableInlineValue icon={FsnNetworkIcon}>
          {networkFulfillment.label}
        </SalesOrderTableInlineValue>
      )
    }

    return (
      <SalesOrderTableActionPill icon={FsnNetworkIcon} tone={tone}>
        {networkFulfillment.label}
      </SalesOrderTableActionPill>
    )
  }

  if (needsFsnNetworkFulfillment(order)) {
    return (
      <SalesOrderTableActionPill icon={FsnNetworkIcon} tone="warning">
        Outside area
      </SalesOrderTableActionPill>
    )
  }

  if (isCancelledSalesOrder(order)) {
    return (
      <SalesOrderTableActionPill icon={CircleX} tone="destructive">
        Cancelled
      </SalesOrderTableActionPill>
    )
  }

  const fulfillmentStatus = Object.values(SalesOrderFulfillmentStatus).find(
    (status) => status.Value === order.fulfillment_status
  )

  if (!fulfillmentStatus) {
    return null
  }

  const isFulfilled = order.fulfillment_status === SalesOrderFulfillmentStatus.Fulfilled.Value

  if (order.fulfillment_status === SalesOrderFulfillmentStatus.ToDo.Value) {
    return null
  }

  const label =
    order.fulfillment_status === SalesOrderFulfillmentStatus.Out.Value
      ? 'Out for delivery'
      : order.fulfillment_status === SalesOrderFulfillmentStatus.Staged.Value
        ? order.fulfillment_type === FulfillmentType.Pickup.Value
          ? 'In progress'
          : 'Ready for delivery'
        : order.fulfillment_status === SalesOrderFulfillmentStatus.InProgress.Value
          ? 'In progress'
          : order.fulfillment_status === SalesOrderFulfillmentStatus.ReadyForPickup.Value
            ? 'Ready for pickup'
            : order.fulfillment_status === SalesOrderFulfillmentStatus.ReadyForDelivery.Value
              ? 'Ready for delivery'
              : fulfillmentStatus.Name

  return (
    <SalesOrderTableQuietStatus
      iconName={fulfillmentStatus.Icon}
      tone={isFulfilled ? 'success' : getActionToneFromBadgeVariant(fulfillmentStatus.Variant)}
    >
      {label}
    </SalesOrderTableQuietStatus>
  )
}

function SalesOrderFulfillmentContent({
  order,
}: {
  order: SalesOrderTableOrder
}): ReactElement | null {
  const pickupLocationName =
    order.fulfillment_type === FulfillmentType.Pickup.Value && order.location
      ? getLocationName(order.location)
      : null

  return (
    <div className="grid justify-items-start gap-y-1">
      <div
        data-sales-order-fulfillment-row="summary"
        className="flex flex-wrap items-center gap-x-2 gap-y-1.5 @[40rem]/index-table:flex-nowrap"
      >
        <SalesOrderFulfillmentMethodContent order={order} />
        <SalesOrderFulfillmentStatusContent order={order} />
      </div>
      {pickupLocationName ? (
        <Typography
          variant="caption"
          align="left"
          data-sales-order-fulfillment-row="location"
          className="block w-full min-w-0 max-w-full truncate @[40rem]/index-table:pl-8"
        >
          {pickupLocationName}
        </Typography>
      ) : null}
    </div>
  )
}

function SalesOrderDueContent({ order }: { order: SalesOrderTableOrder }): ReactElement | null {
  const presentation = getSalesOrderFulfillmentDuePresentation(order)

  if (!presentation) {
    return (
      <Typography variant="supporting" aria-label="No delivery / pickup date">
        —
      </Typography>
    )
  }

  if (presentation.variant === 'destructive' || presentation.variant === 'warning') {
    return (
      <SalesOrderTableActionPill
        tone={presentation.variant === 'destructive' ? 'destructive' : 'warning'}
        icon={Calendar}
      >
        {presentation.label}
      </SalesOrderTableActionPill>
    )
  }

  return (
    <SalesOrderTableQuietStatus icon={Calendar} tone="muted" emphasized>
      {presentation.label}
    </SalesOrderTableQuietStatus>
  )
}

function SalesOrderPaymentContent({ order }: { order: SalesOrderTableOrder }): ReactElement | null {
  const showsRefundBadge = Boolean(order.refund_status)
  const showCancelledPaymentPresentation =
    usesCancelledPaymentPresentation(order) && !showsRefundBadge

  if (showCancelledPaymentPresentation) {
    return (
      <SalesOrderTableActionPill icon={CircleX} tone="destructive">
        Cancelled
      </SalesOrderTableActionPill>
    )
  }

  if (showsRefundBadge) {
    const refundStatus = Object.values(SalesOrderRefundStatus).find(
      (status) => status.Value === order.refund_status
    )

    if (!refundStatus) {
      return null
    }

    const label =
      refundStatus.Value === SalesOrderRefundStatus.PartialRefund.Value
        ? 'Partial refund'
        : refundStatus.Name

    return (
      <SalesOrderTableActionPill
        iconName={refundStatus.Icon}
        tone={
          refundStatus.Value === SalesOrderRefundStatus.Refunded.Value ? 'destructive' : 'refund'
        }
      >
        {label}
      </SalesOrderTableActionPill>
    )
  }

  const paymentStatus = Object.values(SalesOrderPaymentStatus).find(
    (status) => status.Value === order.payment_status
  )

  if (!paymentStatus) {
    return null
  }

  if (paymentStatus.Value === SalesOrderPaymentStatus.Paid.Value) {
    return (
      <SalesOrderTableQuietStatus iconName={paymentStatus.Icon} tone="success" emphasized>
        {paymentStatus.Name}
      </SalesOrderTableQuietStatus>
    )
  }

  const paymentStatusName = paymentStatus.Name
  const label =
    paymentStatus.Value === SalesOrderPaymentStatus.OpenBalance.Value
      ? 'Open balance'
      : paymentStatus.Value === SalesOrderPaymentStatus.PartiallyPaid.Value
        ? 'Partially paid'
        : paymentStatusName

  return (
    <SalesOrderTableActionPill
      icon={
        paymentStatus.Value === SalesOrderPaymentStatus.OpenBalance.Value ? CircleAlert : undefined
      }
      iconName={paymentStatus.Icon}
      tone="warning"
    >
      {label}
    </SalesOrderTableActionPill>
  )
}

function SalesOrderSourceContent({ order }: { order: SalesOrderTableOrder }): ReactElement | null {
  const meta = getSalesOrderSourceMeta(order.source, order.order_source_link)

  return (
    <SalesOrderTableInlineValue
      icon={meta.icon}
      className="overflow-visible font-normal text-muted-foreground"
      iconClassName="size-4 overflow-visible object-contain"
    >
      {meta.label}
      {order.order_source_link?.fsn_change ? (
        <span className="font-semibold text-status-warning-foreground">
          {order.order_source_link.fsn_change.state
            ? ' · Changes need review'
            : ' · FSN status needs attention'}
        </span>
      ) : null}
    </SalesOrderTableInlineValue>
  )
}

function SalesOrderAssigneeContent({
  order,
}: {
  order: SalesOrderTableOrder
}): ReactElement | null {
  const assignee = order.assigned_user

  if (!assignee) {
    return (
      <Typography variant="supporting" aria-label="Unassigned">
        —
      </Typography>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={`Assigned to ${assignee.name}`}
          className="inline-flex min-w-0 items-center gap-2"
        >
          <UserAvatar user={assignee} className="size-7 shrink-0" fallbackClassName="text-xs" />
          <span className="truncate @[40rem]/index-table:hidden">{assignee.name}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{assignee.name}</TooltipContent>
    </Tooltip>
  )
}

function getArrangementPhotoSummary(order: Pick<SalesOrderTableOrder, 'arrangement_photo_status'>) {
  const resolvedStatus: Partial<SalesOrderTableOrder['arrangement_photo_status']> =
    order?.arrangement_photo_status ?? {}
  const count = Number(resolvedStatus.count ?? 0)
  const failedCount = Number(resolvedStatus.failed_count ?? 0)
  const photoLabel = `${count} arrangement ${count === 1 ? 'photo' : 'photos'} uploaded`
  const failureLabel = `${failedCount} failed to send`

  return {
    count,
    failedCount,
    label: count === 0 ? 'No arrangement photos uploaded' : photoLabel,
    tooltip:
      failedCount > 0
        ? `${photoLabel}; ${failureLabel}`
        : count === 0
          ? 'No arrangement photos uploaded'
          : photoLabel,
  }
}

function SalesOrderArrangementPhotoContent({
  order,
}: {
  order: SalesOrderTableOrder
}): ReactElement | null {
  if (order.fulfillment_type === FulfillmentType.InStore.Value) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="text-xs font-normal text-muted-foreground"
            aria-label="Arrangement photos not available"
          >
            —
          </span>
        </TooltipTrigger>
        <TooltipContent>Arrangement photos aren’t available for in-store orders.</TooltipContent>
      </Tooltip>
    )
  }

  const summary = getArrangementPhotoSummary(order)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex min-w-10 items-center justify-center gap-1.5 text-sm tabular-nums',
            summary.failedCount > 0 ? 'text-status-destructive-foreground' : 'text-muted-foreground'
          )}
          aria-label={summary.tooltip}
        >
          <Camera className="size-4" aria-hidden="true" />
          <span className="font-medium">{summary.count}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{summary.tooltip}</TooltipContent>
    </Tooltip>
  )
}

function SalesOrderActionsContent({ order }: { order: SalesOrderTableOrder }): ReactElement | null {
  if (!shouldShowSalesOrderActions(order)) {
    return (
      <div className="flex justify-start lg:justify-end">
        <div className="size-8" />
      </div>
    )
  }

  if (!shouldShowMarkFulfilledAction(order)) {
    return <ReadOnlySalesOrderActionsContent order={order} />
  }

  return <VisibleSalesOrderActionsContent order={order} />
}

function ReadOnlySalesOrderActionsContent({
  order,
}: {
  order: SalesOrderTableOrder
}): ReactElement | null {
  const row = { original: order }

  return (
    <div className="flex justify-start lg:justify-end">
      <SalesOrderActionsMenu row={row} markFulfilledAction={null} />
    </div>
  )
}

function VisibleSalesOrderActionsContent({
  order,
}: {
  order: SalesOrderTableOrder
}): ReactElement | null {
  const row = { original: order }
  const markFulfilledAction = useSalesOrderActionMenu(order)

  return (
    <div className="flex justify-start lg:justify-end">
      <SalesOrderActionsMenu row={row} markFulfilledAction={markFulfilledAction} />
      {markFulfilledAction.dialogs}
    </div>
  )
}

function SalesOrderRecipientContent({
  order,
}: {
  order: SalesOrderTableOrder
}): ReactElement | null {
  const recipientName = String(order.recipient_name ?? '').trim()
  const purchaserNames = [order.purchaser?.name, order.ordering_contact?.name]
    .map((name) => String(name ?? '').trim())
    .filter(
      (name, index, names) =>
        name &&
        name.localeCompare(recipientName, undefined, { sensitivity: 'accent' }) !== 0 &&
        names.indexOf(name) === index
    )
  const purchaserContext = purchaserNames.join(' · ')

  return (
    <>
      {recipientName ? (
        <span
          className={cn('block truncate font-medium', isSalesOrderUnread(order) && 'font-semibold')}
        >
          {recipientName}
        </span>
      ) : (
        <Typography variant="supporting" italic>
          None provided
        </Typography>
      )}
      {purchaserContext ? (
        <Typography
          variant="caption"
          weight="normal"
          className="mt-1 block truncate"
          title={`Purchased by ${purchaserContext}`}
        >
          Purchased by {purchaserContext}
        </Typography>
      ) : null}
    </>
  )
}

export function getSalesOrderColumns(
  showOrganization: boolean,
  showAssignee = false
): SalesOrderColumn[] {
  const columns: (SalesOrderColumn | null)[] = [
    {
      id: 'id',
      header: 'Order',
      cell: ({ row }) => {
        const order = row.original
        const isUnread = isSalesOrderUnread(order)

        return (
          <>
            <span className="inline-flex items-center gap-2">
              {isUnread ? <span className="sr-only">Unread order</span> : null}
              <span className="inline-flex min-h-7 min-w-13 items-center justify-center rounded-full bg-accentSecondary px-2.5 py-1 text-sm font-semibold text-accentSecondary-foreground">
                #{order.id}
              </span>
            </span>
            {order.reference_id ? (
              <Typography variant="caption" className="mt-1 block truncate">
                {order.reference_id}
              </Typography>
            ) : null}
          </>
        )
      },
      meta: {
        label: 'Order',
        kind: 'id',
        icon: Hash,
        sortable: salesOrderSortColumns.id,
      },
    },
    showOrganization
      ? {
          id: 'organization',
          header: 'Organization',
          cell: ({ row }) => (
            <span className="block truncate">
              {row.original.organization_name || 'Organization'}
            </span>
          ),
          meta: {
            label: 'Organization',
            kind: 'primary',
            icon: Store,
            sortable: salesOrderSortColumns.organization,
          },
        }
      : null,
    {
      id: 'recipient',
      header: 'Recipient',
      cell: ({ row }) => <SalesOrderRecipientContent order={row.original} />,
      meta: {
        label: 'Recipient',
        kind: 'primary',
        icon: UserRound,
        sortable: salesOrderSortColumns.recipient,
      },
    },
    {
      id: 'source',
      header: 'Source',
      cell: ({ row }) => <SalesOrderSourceContent order={row.original} />,
      meta: {
        label: 'Source',
        kind: 'text',
        icon: ShoppingBag,
        sortable: salesOrderSortColumns.source,
        align: 'start',
        minWidth: '5.75rem',
        preferredWidth: '7rem',
        contentClassName: '!overflow-visible',
      },
    },
    {
      id: 'fulfillment',
      header: 'Fulfillment',
      cell: ({ row }) => <SalesOrderFulfillmentContent order={row.original} />,
      meta: {
        label: 'Fulfillment',
        kind: 'badge',
        minWidth: '14.5rem',
        preferredWidth: '16rem',
        icon: Truck,
        sortable: salesOrderSortColumns.fulfillmentStatus,
        align: 'start',
        link: 'overlay',
      },
    },
    showAssignee
      ? {
          id: 'assigned',
          header: 'Assigned',
          cell: ({ row }) => <SalesOrderAssigneeContent order={row.original} />,
          meta: {
            label: 'Assigned',
            kind: 'avatar',
            icon: UserRound,
            align: 'center',
            minWidth: '4rem',
            preferredWidth: '4rem',
            link: false,
          },
        }
      : null,
    {
      id: 'due',
      header: 'Delivery / pickup date',
      cell: ({ row }) => <SalesOrderDueContent order={row.original} />,
      meta: {
        label: 'Delivery / pickup date',
        kind: 'date',
        icon: Calendar,
        minWidth: '7.5rem',
        preferredWidth: '8.5rem',
        sortable: salesOrderSortColumns.fulfillmentDueDate,
        align: 'start',
      },
    },
    {
      id: 'arrangementPhotos',
      header: 'Photos',
      cell: ({ row }) => <SalesOrderArrangementPhotoContent order={row.original} />,
      meta: {
        label: 'Photos',
        kind: 'text',
        minWidth: '4rem',
        preferredWidth: '4.5rem',
        icon: Camera,
        sortable: salesOrderSortColumns.arrangementPhotoCount,
        align: 'center',
      },
    },
    {
      id: 'payment',
      header: 'Payment',
      cell: ({ row }) => <SalesOrderPaymentContent order={row.original} />,
      meta: {
        label: 'Payment',
        kind: 'badge',
        minWidth: '6.5rem',
        preferredWidth: '7.5rem',
        icon: CreditCard,
        sortable: salesOrderSortColumns.paymentStatus,
        align: 'center',
      },
    },
    {
      id: 'ordered',
      header: 'Ordered',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs font-normal text-muted-foreground tabular-nums">
          {row.original.created_at
            ? dayjs(row.original.created_at).format('MM/DD/YYYY')
            : 'Not set'}
        </span>
      ),
      meta: {
        label: 'Ordered',
        kind: 'date',
        icon: CalendarClock,
        sortable: salesOrderSortColumns.created,
        align: 'center',
        contentAlign: 'start',
        contentClassName: 'lg:w-[10ch]',
      },
    },
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => <SalesOrderAmountCell order={row.original} />,
      meta: {
        label: 'Total',
        kind: 'money',
        icon: DollarSign,
        sortable: salesOrderSortColumns.total,
        align: 'end',
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => <SalesOrderActionsContent order={row.original} />,
      meta: {
        label: 'Actions',
        kind: 'actions',
        align: 'end',
        headerClassName: 'w-10',
        link: false,
      },
    },
  ]

  return columns.filter((column): column is SalesOrderColumn => Boolean(column))
}

export const SalesOrdersIndexTable = memo(function SalesOrdersIndexTable({
  orders,
  response,
  page,
  pageSize,
  sort,
  toolbar,
  isLoading,
  showAssignee = false,
  onPageChange,
  onPageIntent,
  onPageSizeChange,
  onSortChange,
}: {
  orders?: SalesOrderTableOrder[]
  response?: IndexPaginationResponse | null
  page: number
  pageSize: number
  sort?: string | null
  toolbar?: ReactNode
  isLoading?: boolean
  showAssignee?: boolean
  onPageChange: IndexTablePaginationProps['onPageChange']
  onPageIntent?: IndexTablePaginationProps['onPageIntent']
  onPageSizeChange: IndexTablePaginationProps['onPageSizeChange']
  onSortChange?: IndexDataTableProps<SalesOrderTableOrder>['onSortChange']
}): ReactElement {
  const path = usePathHelper()
  const ability = useAbility()
  const isGlobalOrg = useIsGlobalOrg()
  const showOrganization = ability.can('access', 'global') && isGlobalOrg
  const paginationMeta = getIndexPaginationMeta(response, page, pageSize)
  const columns = useMemo(
    () => getSalesOrderColumns(showOrganization, showAssignee),
    [showAssignee, showOrganization]
  )

  return (
    <div className="space-y-4">
      <IndexDataTable
        data={orders}
        columns={columns}
        sort={sort}
        onSortChange={onSortChange}
        rowLink={(order) =>
          path.salesOrders.show(order.id).using({ orgId: order.organization_id }).link
        }
        getRowPresentation={getSalesOrderRowPresentation}
        getRowId={(order) => String(order.id)}
        toolbar={toolbar}
        isLoading={isLoading}
        emptyState={<Typography variant="supporting">No orders found.</Typography>}
        tableClassName="text-sm"
      />

      <IndexTablePagination
        meta={paginationMeta}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageIntent={onPageIntent}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  )
})
